import { useCallback, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AuthContext } from "../../../../contexts/AuthContext";
import { reportService } from "../../../../services/api/reportService";
import { hasChartData } from "../../components/EmptyChart";
import {
    formatDateParam,
    generateChartImage,
    buildPrintHTML,
    buildReportTableSection,
    toBase64FromPublic
} from "../reportUtils";
import { getBackendUrl } from "../../../../utils/env";
import ProfileReportPresenter from "./ProfileReportPresenter";

const PAGE_SIZE = 10;

const ProfileReportTab = () => {
    const { token_client } = useContext(AuthContext);
    const [tempFromDate, setTempFromDate] = useState("");
    const [tempToDate, setTempToDate] = useState("");
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const [profileReport, setProfileReport] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [fullData, setFullData] = useState([]);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const fetchReport = useCallback(async (fDate, tDate) => {
        try {
            setLoading(true);
            const params = {
                fromDate: formatDateParam(fDate),
                toDate: formatDateParam(tDate),
                serviceId: "",
                status: "",
            };

            const res = await reportService.get_report_client_profile(token_client, params);
            setProfileReport(res);
            const data = res?.data || [];
            setFullData(data);
            setTableData(data.slice(0, PAGE_SIZE));
            setHasMore(data.length > PAGE_SIZE);
        } catch (err) {
            toast.error("Không thể tải báo cáo hồ sơ");
        } finally {
            setLoading(false);
        }
    }, [token_client]);

    useEffect(() => {
        fetchReport("", "");
    }, [fetchReport]);

    const loadMoreData = () => {
        if (loadingMore) return;
        setLoadingMore(true);
        setTimeout(() => {
            const currentLength = tableData.length;
            const nextData = fullData.slice(0, currentLength + PAGE_SIZE);
            setTableData(nextData);
            setHasMore(nextData.length < fullData.length);
            setLoadingMore(false);
        }, 500);
    };

    const handleApplyFilter = () => {
        fetchReport(tempFromDate, tempToDate);
    };

    const handleExport = async () => {
        try {
            setExporting(true);
            const chartImg = hasChartData(profileReport?.chart)
                ? await generateChartImage(profileReport.chart, "Biểu đồ hồ sơ")
                : "";

            let bodyHTML = `<h1 class="heading">BÁO CÁO HỒ SƠ</h1>`;
            if (tempFromDate || tempToDate) {
                bodyHTML += `<div style="font-size:12px;color:#374151;margin-bottom:20px;text-align:center"><b>Thời gian:</b> ${tempFromDate || "..."} → ${tempToDate || "..."}</div>`;
            }

            const printData = (profileReport?.serviceSummaryForPrint && profileReport.serviceSummaryForPrint.length > 0)
                ? profileReport.serviceSummaryForPrint
                : null;

            const tableHTML = printData
                ? printData.map(group => `
                    <h3 style="margin: 20px 0 10px; color: #0457C2; border-bottom: 1px solid #eee; padding-bottom: 5px;">
                        ${group.serviceName} (${group.totalProfiles})
                    </h3>
                    <table>
                        <thead>
                            <tr>
                                <th style="width:50px">STT</th>
                                <th>Tên hồ sơ</th>
                                <th style="width:120px">Mã hồ sơ</th>
                                <th style="width:120px">Trạng thái</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${group.profiles.map((p, pIdx) => `
                                <tr>
                                    <td>${pIdx + 1}</td>
                                    <td>${p.profileName || "-"}</td>
                                    <td>${p.profileCode || "-"}</td>
                                    <td>${p.status || "-"}</td>
                                </tr>
                            `).join("")}
                        </tbody>
                    </table>
                `).join("")
                : `
                    <table>
                        <thead>
                            <tr>
                                <th style="width:50px">STT</th>
                                <th>Tên hồ sơ</th>
                                <th style="width:120px">Mã hồ sơ</th>
                                <th>Dịch vụ</th>
                                <th style="width:120px">Trạng thái</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${fullData.length > 0
                                ? fullData.map((item, idx) => `
                                <tr>
                                    <td>${idx + 1}</td>
                                    <td>${item.profileName || ""}</td>
                                    <td>${item.profileCode || "-"}</td>
                                    <td>${item.serviceName || ""}</td>
                                    <td>${item.status || ""}</td>
                                </tr>
                            `).join("")
                                : `<tr><td colspan="5" style="text-align:center;padding:20px;color:#6b7280">Không có dữ liệu trong khoảng thời gian này</td></tr>`
                            }
                        </tbody>
                    </table>
                `;

            bodyHTML += `
                <div class="report-section">
                    <div style="margin-bottom:12px">
                        <div class="summary-box"><b>Tổng hồ sơ:</b> ${profileReport?.summary?.totalProfiles || 0}</div>
                        <div class="summary-box"><b>Hoàn thành:</b> ${profileReport?.summary?.completedProfiles || 0}</div>
                        <div class="summary-box"><b>Chưa hoàn thành:</b> ${profileReport?.summary?.unCompletedProfiles || 0}</div>
                    </div>
                    ${chartImg ? `<div class="chart-container"><img src="${chartImg}" alt="Chart" /></div>` : ""}
                </div>
                ${buildReportTableSection(tableHTML)}
            `;

            const html = buildPrintHTML(bodyHTML, "Báo cáo hồ sơ");
            const watermarkBase64 = await toBase64FromPublic("/images/watermark-45.png");
            const BE_URL = getBackendUrl();

            const response = await fetch(`${BE_URL}/pdf/generate`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token_client}`,
                },
                body: JSON.stringify({
                    html,
                    profileName: "Báo cáo hồ sơ",
                    watermarkBase64,
                    watermarkOpacity: 0.12,
                    options: { width: 1123, height: 794, deviceScaleFactor: 2 },
                    watermarkMode: "fixed",
                }),
            });

            if (!response.ok) throw new Error("Không thể tạo PDF");

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Bao-cao-ho-so-${Date.now()}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Xuất báo cáo thành công!");
        } catch (err) {
            console.error("Export error:", err);
            toast.error("Xuất PDF thất bại");
        } finally {
            setExporting(false);
        }
    };

    return (
        <ProfileReportPresenter
            loading={loading}
            exporting={exporting}
            tempFromDate={tempFromDate}
            tempToDate={tempToDate}
            setTempFromDate={setTempFromDate}
            setTempToDate={setTempToDate}
            handleApplyFilter={handleApplyFilter}
            handleExport={handleExport}
            profileReport={profileReport}
            tableData={tableData}
            hasMore={hasMore}
            loadingMore={loadingMore}
            loadMoreData={loadMoreData}
        />
    );
};

export default ProfileReportTab;
